
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';
import ChartLegend from '@/components/charts/ChartLegend';
import { Device } from '@/components/device-health/gis-map/types';

interface DeviceStatusChartProps {
  chartData: any[];
  selectedSection: string | null;
  onCardClick: (section: string | null) => void;
  sectionName: string;
  deviceData?: Device | null;
}

const DeviceStatusChart: React.FC<DeviceStatusChartProps> = ({
  chartData,
  selectedSection,
  onCardClick,
  sectionName,
  deviceData
}) => {
  // State to track which lines are visible
  const [hiddenLines, setHiddenLines] = useState<string[]>([]);

  // Toggle visibility of a line when legend is clicked
  const handleLegendClick = (dataKey: string) => {
    setHiddenLines(prevHiddenLines => 
      prevHiddenLines.includes(dataKey)
        ? prevHiddenLines.filter(key => key !== dataKey)
        : [...prevHiddenLines, dataKey]
    );
  };

  // Customize title if we have device data
  const getTitle = () => {
    if (deviceData) {
      return `Device Status Trend: ${deviceData.name} (units)`;
    }
    return "Device Status Trend (units)";
  };

  return (
    <Card 
      className={cn(
        "mb-6",
        selectedSection === sectionName && "border-blue-500 ring-1 ring-blue-500"
      )}
    >
      <CardHeader className="pb-2 flex flex-row justify-between">
        <CardTitle className="text-lg">{getTitle()}</CardTitle>
        {selectedSection === sectionName && (
          <Button 
            variant="outline" 
            size="sm" 
            className="text-xs h-7"
            onClick={() => onCardClick(null)}
          >
            Clear selection
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip 
                formatter={(value, name) => {
                  if (name === 'totalOnline') return [`${value} devices`, 'Online'];
                  if (name === 'totalWarning') return [`${value} devices`, 'Warning'];
                  if (name === 'totalCritical') return [`${value} devices`, 'Critical'];
                  if (name === 'totalOffline') return [`${value} devices`, 'Offline'];
                  return [value, name];
                }}
                contentStyle={{ 
                  borderRadius: '8px', 
                  border: 'none', 
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' 
                }}
              />
              <Line 
                type="monotone" 
                dataKey="totalOnline" 
                name="Total Online" 
                stroke="#10b981" 
                activeDot={{ r: 8 }} 
                hide={hiddenLines.includes('totalOnline')}
              />
              <Line 
                type="monotone" 
                dataKey="totalWarning" 
                name="Total Warning" 
                stroke="#f59e0b" 
                hide={hiddenLines.includes('totalWarning')}
              />
              <Line 
                type="monotone" 
                dataKey="totalCritical" 
                name="Total Critical" 
                stroke="#ef4444" 
                hide={hiddenLines.includes('totalCritical')}
              />
              <Line 
                type="monotone" 
                dataKey="totalOffline" 
                name="Total Offline" 
                stroke="#6b7280" 
                hide={hiddenLines.includes('totalOffline')}
              />
            </LineChart>
          </ResponsiveContainer>
          
          {/* Render the custom legend */}
          <ChartLegend 
            payload={[
              { dataKey: 'totalOnline', value: 'Total Online (devices)', color: '#10b981' },
              { dataKey: 'totalWarning', value: 'Total Warning (devices)', color: '#f59e0b' },
              { dataKey: 'totalCritical', value: 'Total Critical (devices)', color: '#ef4444' },
              { dataKey: 'totalOffline', value: 'Total Offline (devices)', color: '#6b7280' }
            ]} 
            hiddenLines={hiddenLines}
            onLegendClick={handleLegendClick}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default DeviceStatusChart;
